import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  User,
  Mail,
  Phone,
  Building2,
  MapPin,
  Calendar,
  Shield,
  Bell,
  Save,
  Edit2,
  X,
  CheckCircle,
  AlertCircle,
  Loader2,
  CreditCard,
  Clock,
  Globe,
} from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { Avatar } from '../components/ui/Avatar';
import { api } from '../lib/api';
import { ENDPOINTS } from '../config/api';
import { cn } from '../lib/utils';

// Types
interface Address {
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
}

interface Preferences {
  emailNotifications?: boolean;
  pushNotifications?: boolean;
  smsNotifications?: boolean;
  transactionAlerts?: boolean;
  marketingEmails?: boolean;
  language?: string;
  timezone?: string;
}

interface UserProfile {
  id: string;
  tenantId: string;
  name: string;
  email: string;
  cpfCnpj: string;
  documentType: 'CPF' | 'CNPJ';
  phone: string | null;
  type: string;
  status: string;
  avatarUrl: string | null;
  tenantName: string | null;
  virtualAccountId: string | null;
  address: Address | null;
  preferences: Preferences | null;
  createdAt: string;
  lastLoginAt: string | null;
}

// Fetch profile hook
function useProfile() {
  return useQuery<UserProfile>({
    queryKey: ['profile'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.users.profile);
      return response.data;
    },
  });
}

// Update profile hook
function useUpdateProfile() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: { name?: string; phone?: string; address?: Address; preferences?: Preferences }) => {
      const response = await api.put(ENDPOINTS.users.updateProfile, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}

// Format document (CPF/CNPJ)
function formatDocument(doc: string, type: 'CPF' | 'CNPJ'): string {
  if (type === 'CPF') {
    return doc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  return doc.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
}

// Format date
function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

// User type labels
const userTypeLabels: Record<string, string> = {
  ADMIN: 'Administrador',
  OPERATOR: 'Operador',
  COMPLIANCE: 'Compliance',
  EMPLOYEE: 'Funcionário',
  CUSTOMER: 'Correntista',
  SUPPLIER: 'Fornecedor',
};

// Status badges
const statusBadges: Record<string, { variant: 'success' | 'warning' | 'error'; label: string }> = {
  ACTIVE: { variant: 'success', label: 'Ativo' },
  INACTIVE: { variant: 'error', label: 'Inativo' },
  BLOCKED: { variant: 'error', label: 'Bloqueado' },
  PENDING: { variant: 'warning', label: 'Pendente' },
};

export function Profile() {
  const { data: profile, isLoading, error } = useProfile();
  const updateProfile = useUpdateProfile();
  
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<{
    name: string;
    phone: string;
    address: Address;
  }>({
    name: '',
    phone: '',
    address: {},
  });

  // Start editing
  const handleStartEdit = () => {
    if (profile) {
      setEditData({
        name: profile.name,
        phone: profile.phone || '',
        address: profile.address || {},
      });
      setIsEditing(true);
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditData({ name: '', phone: '', address: {} });
  };

  // Save changes
  const handleSave = async () => {
    try {
      await updateProfile.mutateAsync({
        name: editData.name,
        phone: editData.phone || undefined,
        address: Object.keys(editData.address).length > 0 ? editData.address : undefined,
      });
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating profile:', err);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  // Error state
  if (error || !profile) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md text-center p-8">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-400" />
          <h2 className="text-xl font-semibold text-surface-100 mb-2">
            Erro ao carregar perfil
          </h2>
          <p className="text-surface-400">
            Não foi possível carregar seus dados. Tente novamente mais tarde.
          </p>
        </Card>
      </div>
    );
  }

  const statusBadge = statusBadges[profile.status] || { variant: 'info' as const, label: profile.status };

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
            <User className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-surface-100">Meu Perfil</h1>
            <p className="text-surface-400">Gerencie suas informações pessoais</p>
          </div>
        </div>
        {!isEditing ? (
          <Button onClick={handleStartEdit}>
            <Edit2 className="w-4 h-4 mr-2" />
            Editar
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={handleCancelEdit}>
              <X className="w-4 h-4 mr-2" />
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={updateProfile.isPending}>
              {updateProfile.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Salvar
            </Button>
          </div>
        )}
      </div>

      {/* Profile Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Avatar & Basic Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-1"
        >
          <Card variant="profile" hover glow className="h-full">
            <div className="flex flex-col items-center text-center">
              {/* Avatar */}
              <div className="relative mb-4">
                <Avatar
                  src={profile.avatarUrl || undefined}
                  name={profile.name}
                  size="xl"
                  className="w-24 h-24 text-2xl"
                />
                <div className={cn(
                  'absolute bottom-1 right-1 w-5 h-5 rounded-full border-2 border-surface-900',
                  profile.status === 'ACTIVE' ? 'bg-green-500' : 'bg-surface-500'
                )} />
              </div>
              
              {/* Name */}
              <h2 className="text-xl font-bold text-surface-100 mb-1">
                {profile.name}
              </h2>
              
              {/* Type Badge */}
              <Badge variant="primary" className="mb-3">
                {userTypeLabels[profile.type] || profile.type}
              </Badge>
              
              {/* Status */}
              <Badge variant={statusBadge.variant} size="sm">
                {statusBadge.label}
              </Badge>
              
              {/* Tenant Info */}
              {profile.tenantName && (
                <div className="mt-4 pt-4 border-t border-surface-800 w-full">
                  <div className="flex items-center justify-center gap-2 text-surface-400">
                    <Building2 className="w-4 h-4" />
                    <span className="text-sm">{profile.tenantName}</span>
                  </div>
                </div>
              )}
              
              {/* Member Since */}
              <div className="mt-4 flex items-center gap-2 text-surface-500 text-sm">
                <Calendar className="w-4 h-4" />
                <span>Membro desde {formatDate(profile.createdAt)}</span>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Personal Information Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:col-span-2"
        >
          <Card variant="profile" hover glow>
            <CardHeader className="px-0 pt-0">
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary-400" />
                Informações Pessoais
              </CardTitle>
              <CardDescription>
                Seus dados cadastrais na plataforma
              </CardDescription>
            </CardHeader>
            <CardBody className="px-0 pb-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Name */}
                <div className="space-y-2">
                  <label className="text-sm text-surface-400">Nome Completo</label>
                  {isEditing ? (
                    <Input
                      value={editData.name}
                      onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                      placeholder="Seu nome"
                    />
                  ) : (
                    <p className="text-surface-100 font-medium">{profile.name}</p>
                  )}
                </div>
                
                {/* Email */}
                <div className="space-y-2">
                  <label className="text-sm text-surface-400">E-mail</label>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-surface-500" />
                    <p className="text-surface-100">{profile.email}</p>
                  </div>
                </div>
                
                {/* Phone */}
                <div className="space-y-2">
                  <label className="text-sm text-surface-400">Telefone</label>
                  {isEditing ? (
                    <Input
                      value={editData.phone}
                      onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                      placeholder="(00) 00000-0000"
                    />
                  ) : (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-surface-500" />
                      <p className="text-surface-100">{profile.phone || 'Não informado'}</p>
                    </div>
                  )}
                </div>
                
                {/* Document */}
                <div className="space-y-2">
                  <label className="text-sm text-surface-400">{profile.documentType}</label>
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-surface-500" />
                    <p className="text-surface-100">
                      {formatDocument(profile.cpfCnpj, profile.documentType)}
                    </p>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </motion.div>
      </div>

      {/* Address Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card variant="profile" hover glow>
          <CardHeader className="px-0 pt-0">
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary-400" />
              Endereço
            </CardTitle>
            <CardDescription>
              Seu endereço de correspondência
            </CardDescription>
          </CardHeader>
          <CardBody className="px-0 pb-0">
            {isEditing ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2 space-y-2">
                  <label className="text-sm text-surface-400">Rua</label>
                  <Input
                    value={editData.address.street || ''}
                    onChange={(e) => setEditData({
                      ...editData,
                      address: { ...editData.address, street: e.target.value }
                    })}
                    placeholder="Nome da rua"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-surface-400">Número</label>
                  <Input
                    value={editData.address.number || ''}
                    onChange={(e) => setEditData({
                      ...editData,
                      address: { ...editData.address, number: e.target.value }
                    })}
                    placeholder="Nº"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-surface-400">Complemento</label>
                  <Input
                    value={editData.address.complement || ''}
                    onChange={(e) => setEditData({
                      ...editData,
                      address: { ...editData.address, complement: e.target.value }
                    })}
                    placeholder="Apto, Sala, etc."
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-surface-400">Bairro</label>
                  <Input
                    value={editData.address.neighborhood || ''}
                    onChange={(e) => setEditData({
                      ...editData,
                      address: { ...editData.address, neighborhood: e.target.value }
                    })}
                    placeholder="Bairro"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-surface-400">CEP</label>
                  <Input
                    value={editData.address.zipCode || ''}
                    onChange={(e) => setEditData({
                      ...editData,
                      address: { ...editData.address, zipCode: e.target.value }
                    })}
                    placeholder="00000-000"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-surface-400">Cidade</label>
                  <Input
                    value={editData.address.city || ''}
                    onChange={(e) => setEditData({
                      ...editData,
                      address: { ...editData.address, city: e.target.value }
                    })}
                    placeholder="Cidade"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-surface-400">Estado</label>
                  <Input
                    value={editData.address.state || ''}
                    onChange={(e) => setEditData({
                      ...editData,
                      address: { ...editData.address, state: e.target.value }
                    })}
                    placeholder="UF"
                  />
                </div>
              </div>
            ) : (
              <div>
                {profile.address && Object.keys(profile.address).some(k => (profile.address as any)[k]) ? (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-surface-500 mt-0.5" />
                    <div>
                      <p className="text-surface-100">
                        {profile.address.street}{profile.address.number ? `, ${profile.address.number}` : ''}
                        {profile.address.complement ? ` - ${profile.address.complement}` : ''}
                      </p>
                      <p className="text-surface-400">
                        {profile.address.neighborhood}
                        {profile.address.city ? ` - ${profile.address.city}` : ''}
                        {profile.address.state ? `/${profile.address.state}` : ''}
                      </p>
                      {profile.address.zipCode && (
                        <p className="text-surface-500 text-sm">CEP: {profile.address.zipCode}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-surface-500 italic">Endereço não informado</p>
                )}
              </div>
            )}
          </CardBody>
        </Card>
      </motion.div>

      {/* Account Info Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card variant="profile" hover glow>
          <CardHeader className="px-0 pt-0">
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary-400" />
              Informações da Conta
            </CardTitle>
            <CardDescription>
              Dados da sua conta na plataforma (somente leitura)
            </CardDescription>
          </CardHeader>
          <CardBody className="px-0 pb-0">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className="text-sm text-surface-400">ID do Usuário</label>
                <p className="text-surface-100 font-mono text-sm bg-surface-800 px-3 py-2 rounded-lg">
                  {profile.id.slice(0, 8)}...
                </p>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm text-surface-400">Tipo de Conta</label>
                <p className="text-surface-100">{userTypeLabels[profile.type] || profile.type}</p>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm text-surface-400">Status</label>
                <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
              </div>
              
              {profile.virtualAccountId && (
                <div className="space-y-2">
                  <label className="text-sm text-surface-400">Conta Virtual</label>
                  <p className="text-surface-100 font-mono text-sm bg-surface-800 px-3 py-2 rounded-lg">
                    {profile.virtualAccountId.slice(0, 8)}...
                  </p>
                </div>
              )}
              
              <div className="space-y-2">
                <label className="text-sm text-surface-400">Membro desde</label>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-surface-500" />
                  <p className="text-surface-100">{formatDate(profile.createdAt)}</p>
                </div>
              </div>
              
              {profile.lastLoginAt && (
                <div className="space-y-2">
                  <label className="text-sm text-surface-400">Último acesso</label>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-surface-500" />
                    <p className="text-surface-100">{formatDate(profile.lastLoginAt)}</p>
                  </div>
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      </motion.div>

      {/* Preferences Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Card variant="profile" hover glow>
          <CardHeader className="px-0 pt-0">
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-primary-400" />
              Preferências de Notificação
            </CardTitle>
            <CardDescription>
              Configure como deseja receber notificações
            </CardDescription>
          </CardHeader>
          <CardBody className="px-0 pb-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { key: 'emailNotifications', label: 'Notificações por E-mail', icon: Mail },
                { key: 'pushNotifications', label: 'Notificações Push', icon: Bell },
                { key: 'smsNotifications', label: 'Notificações por SMS', icon: Phone },
                { key: 'transactionAlerts', label: 'Alertas de Transação', icon: AlertCircle },
                { key: 'marketingEmails', label: 'E-mails de Marketing', icon: Mail },
              ].map(({ key, label, icon: Icon }) => (
                <div
                  key={key}
                  className={cn(
                    'flex items-center gap-3 p-3 rounded-lg border transition-colors',
                    profile.preferences?.[key as keyof Preferences]
                      ? 'bg-primary-500/10 border-primary-500/30'
                      : 'bg-surface-800/50 border-surface-700'
                  )}
                >
                  <Icon className={cn(
                    'w-5 h-5',
                    profile.preferences?.[key as keyof Preferences]
                      ? 'text-primary-400'
                      : 'text-surface-500'
                  )} />
                  <span className="text-surface-200 text-sm">{label}</span>
                  {profile.preferences?.[key as keyof Preferences] ? (
                    <CheckCircle className="w-4 h-4 text-green-400 ml-auto" />
                  ) : (
                    <X className="w-4 h-4 text-surface-500 ml-auto" />
                  )}
                </div>
              ))}
              
              {/* Language & Timezone */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-surface-800/50 border border-surface-700">
                <Globe className="w-5 h-5 text-surface-500" />
                <div>
                  <span className="text-surface-200 text-sm block">Idioma</span>
                  <span className="text-surface-400 text-xs">
                    {profile.preferences?.language || 'pt-BR'}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 rounded-lg bg-surface-800/50 border border-surface-700">
                <Clock className="w-5 h-5 text-surface-500" />
                <div>
                  <span className="text-surface-200 text-sm block">Fuso Horário</span>
                  <span className="text-surface-400 text-xs">
                    {profile.preferences?.timezone || 'America/Sao_Paulo'}
                  </span>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      </motion.div>
    </div>
  );
}

export default Profile;
